import { isNumber } from 'lodash';
import { getTrackFunc, type IExportLayerOriginInfo, type IFramework } from '@/framework';
import { asyncTraverseTree, traverseTrees } from '@/utils/tree-kit';
import { BaseLayerViewType, DirectionType, LayerType, type ILayerProps } from '@/version-types';

export class ModifyBasicProperties {
    /** 记录初始的图层 */
    private prevLayer: ILayerProps | null = null;
    /** 记录初始的图层原始信息 */
    private preOriginInfo: IExportLayerOriginInfo | null = null;

    constructor(
        private framework: IFramework,
        private identity: string,
    ) {}

    /**
     * 初始化图层信息
     *
     * @param layerId - 图层ID
     * @returns 返回图层信息和原始信息
     */
    private initLayerInfo(layerId: string, baseLayerViewType: BaseLayerViewType) {
        //初始化的时候，禁止操作
        if (!this.prevLayer) {
            //HACK: 在初始化的时候，禁止操作,不能使用isBaseOperate，图片特效会有问题
            this.framework.service.editor.setDisableScale(true);
            this.framework.service.editor.setDisableRotate(true);
            this.framework.service.editor.setDisableResize(true);
        }

        this.prevLayer = this.prevLayer || this.framework.service.flatLayer.getLayer(layerId, true);
        this.preOriginInfo = this.preOriginInfo || this.framework.service.frame.exportLayerOriginInfo(layerId);

        const { childOriginInfoMap, originInfo } = this.preOriginInfo;

        if (!this.prevLayer || !originInfo) return null;

        this.framework.service.frame.__emitBeforeBaseOperate(this.prevLayer, baseLayerViewType);

        return {
            prevLayer: this.prevLayer,
            originInfo,
            childOriginInfoMap,
        };
    }

    /**
     * 重置图层信息
     */
    private resetLayerInfo() {
        this.prevLayer = null;
        this.preOriginInfo = null;
        this.framework.service.editor.setDisableScale(false);
        this.framework.service.editor.setDisableRotate(false);
        this.framework.service.editor.setDisableResize(false);
    }

    /**
     * 图层尺寸调整
     * @param layerId - 图层 ID
     * @param size - 尺寸
     */
    async size(
        layerId: string,
        data: Partial<Pick<ILayerProps, 'width' | 'height'>> & { isRatio?: boolean },
        options: { writeHistory: boolean; isStart: boolean },
    ) {
        const { isRatio, width, height } = data;
        const layerInfo = this.initLayerInfo(layerId, isRatio ? BaseLayerViewType.SCALE : BaseLayerViewType.RESIZE);
        if (!layerInfo) return;

        //HACK:在移动的过程中 图片特效需要设置isBaseOperate为false进行隐藏
        this.framework.service.editor.setIsBaseOperate(true);

        const { childOriginInfoMap, originInfo, prevLayer } = layerInfo;

        if (isRatio) {
            this.framework.service.frame.updateLayerScale(
                layerId,
                width! - prevLayer.width,
                height! - prevLayer.height,
                { width: prevLayer.width, height: prevLayer.height },
                DirectionType.POSITIVE,
                false,
                originInfo,
                childOriginInfoMap,
                {
                    isStart: options.isStart,
                    isEnd: options.writeHistory ? true : false,
                },
                true,
            );
        } else {
            const offset = isNumber(width) ? width - prevLayer.width : height! - prevLayer.height;
            const isChangeHeight = isNumber(width) ? false : true;
            this.framework.service.frame.updateLayerResize(
                layerId,
                offset,
                DirectionType.POSITIVE,
                false,
                isChangeHeight,
                originInfo,
                childOriginInfoMap,
                {
                    isStart: options.isStart,
                    isEnd: options.writeHistory ? true : false,
                },
            );
        }
        if (options?.writeHistory) {
            asyncTraverseTree(prevLayer, async (layer) => {
                await this.framework.service.behavior
                    .get(layer)
                    ?.[isRatio ? 'onBaseScaleEnd' : 'onBaseResizeEnd']?.(layer.id);
            }).then(() => {
                const layer = this.framework.service.flatLayer.getLayer(prevLayer.id, true);
                if (layer) {
                    this.framework.service.frame.addOrUpdateLayer(layer);
                }
                this.framework.service.history.write(
                    prevLayer,
                    getTrackFunc(this.identity, prevLayer, isRatio ? 'scale' : 'resize'),
                );
            });
            this.framework.service.editor.setIsBaseOperate(false);
            this.framework.service.frame.setActiveLayer(layerId);
            this.resetLayerInfo();
            return prevLayer;
        }
    }
    /**
     * 图层旋转
     * @param layerId - 图层 ID
     * @param rotate - 旋转
     */
    rotate(layerId: string, rotate: number, options = { writeHistory: true }) {
        const layerInfo = this.initLayerInfo(layerId, BaseLayerViewType.ROTATE);
        if (!layerInfo) return;
        const { prevLayer, originInfo, childOriginInfoMap } = layerInfo;

        this.framework.service.frame.updateLayerRotate(
            layerId,
            rotate - prevLayer.rotate,
            originInfo,
            childOriginInfoMap,
            {
                isStart: false,
                isEnd: options?.writeHistory ?? false,
            },
        );

        this.framework.service.frame.addOrUpdateLayer(this.framework.service.flatLayer.getLayer(layerId, true));

        if (options?.writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerId, true);
            this.framework.service.frame.setActiveLayer(layerId);
            this.framework.service.history.write(
                prevLayer,
                nextLayer,
                getTrackFunc(this.identity, prevLayer, 'rotate'),
            );
            this.resetLayerInfo();
            return prevLayer;
        }
    }
    /**
     * 图层位置调整
     * @param layerId - 图层 ID
     * @param position - 位置
     */
    position(layerId: string, position: Partial<Pick<ILayerProps, 'left' | 'top'>>, options = { writeHistory: true }) {
        const layerInfo = this.initLayerInfo(layerId, BaseLayerViewType.MOVE);
        if (!layerInfo) return;
        const { prevLayer, originInfo, childOriginInfoMap } = layerInfo;

        this.framework.service.frame.updateLayerPosition(
            layerId,
            isNumber(position.left) ? position.left - prevLayer.left : 0,
            isNumber(position.top) ? position.top - prevLayer.top : 0,
            originInfo,
            childOriginInfoMap,
            {
                isStart: false,
                isEnd: options?.writeHistory ?? false,
            },
        );
        this.framework.service.frame.addOrUpdateLayer(this.framework.service.flatLayer.getLayer(layerId, true));

        if (options?.writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerId, true);
            this.framework.service.frame.setActiveLayer(layerId);
            this.framework.service.history.write(prevLayer, nextLayer);
            this.resetLayerInfo();
        }
    }

    /**
     * 图层拉伸
     * @param layerId - 图层 ID
     * @param isRatio - 是否等比拉伸
     */
    stretch(layerId: string, isRatio: boolean) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!layer) return;
        layer.extraInfo = {
            ...layer.extraInfo,
            lockRatio: isRatio,
        };
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layerId);
    }

    /**
     * 批量更新图层尺寸
     * @param layerId - 图层 ID
     * @param data - 尺寸
     * @param options - 选项
     */
    batchSize(
        layerId: string,
        data: { width: number; height: never } | { width: never; height: number },
        options: { writeHistory: boolean; isStart: boolean },
    ) {
        const layerInfo = this.initLayerInfo(layerId, BaseLayerViewType.SCALE);
        if (!layerInfo) return;

        //HACK:在移动的过程中 图片特效需要设置isBaseOperate为false进行隐藏
        this.framework.service.editor.setIsBaseOperate(true);

        const { prevLayer } = layerInfo;
        const { width, height } = data;

        traverseTrees(prevLayer.children, (layer) => {
            if (layer.layerType === LayerType.GROUP_LAYER) return;

            const ratio = layer.width / layer.height;
            const size = isNumber(width) ? { width, height: width / ratio } : { width: height * ratio, height: height };
            const layerInfo = this.framework.service.frame.exportLayerOriginInfo(layer.id);
            const { childOriginInfoMap, originInfo } = layerInfo;
            if (!originInfo) return;
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                this.framework.service.frame.updateLayerScale(
                    layer.id,
                    size.width - layer.width,
                    size.height - layer.height,
                    { width: layer.width, height: layer.height },
                    DirectionType.POSITIVE,
                    false,
                    originInfo,
                    childOriginInfoMap,
                    {
                        isStart: options.isStart,
                        isEnd: options.writeHistory ? true : false,
                    },
                    true,
                    isNumber(data.height) ? false : true,
                );
            } else {
                const offset = isNumber(width) ? width - layer.width : height - layer.height;
                const isChangeHeight = isNumber(width) ? false : true;
                this.framework.service.frame.updateLayerResize(
                    layer.id,
                    offset,
                    DirectionType.POSITIVE,
                    false,
                    isChangeHeight,
                    originInfo,
                    childOriginInfoMap,
                    {
                        isStart: options.isStart,
                        isEnd: options.writeHistory ? true : false,
                    },
                );
            }
        });

        if (options?.writeHistory) {
            asyncTraverseTree(prevLayer, async (layer) => {
                await this.framework.service.behavior.get(layer)?.onBaseScaleEnd?.(layer.id);
            }).then(() => {
                this.framework.service.history.write(prevLayer, getTrackFunc(this.identity, prevLayer, 'scale'));
            });
            this.framework.service.editor.setIsBaseOperate(false);
            this.framework.service.frame.addOrUpdateLayer(this.framework.service.flatLayer.getLayer(layerId, true));
            this.framework.service.frame.setActiveLayer(layerId);
            this.resetLayerInfo();
            return prevLayer;
        }
    }
}
